import { useCallback, useEffect, useMemo, useRef } from "react";
import useWebSocket, {
  ReadyState,
  resetGlobalState,
} from "react-use-websocket";
import { WebSocketConnectionStatus } from "@hilos/types/hilos";
import { buildAPIRoute } from "../router/router";
import useHilosStore from "./useHilosStore";

interface UseHilosWebSocketsParams {
  path: string;
  connect?: boolean;
  trackConnectionStatus?: boolean;
  params?: { [key: string]: string | undefined };
}

const BASE_SOCKET_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_PROD_WS_LOCATION
    : process.env.REACT_APP_DEV_WS_LOCATION;

const MAX_RETRIES = 5;
const MANUAL_RECONNECT_CODE = 4200;

// Map ReadyState to our WebSocketConnectionStatus type
function readyStateToStatus(
  readyState: ReadyState
): WebSocketConnectionStatus {
  switch (readyState) {
    case ReadyState.CONNECTING:
      return "connecting";
    case ReadyState.OPEN:
      return "connected";
    case ReadyState.CLOSING:
    case ReadyState.CLOSED:
      return "disconnected";
    default:
      return "disconnected";
  }
}

function useHilosWebSockets({
  path,
  connect = true,
  trackConnectionStatus = false,
  params = {},
}: UseHilosWebSocketsParams) {
  const reconnectTrigger = useHilosStore(
    (state) => state.webSocketState.reconnectTrigger
  );
  const retryCountRef = useRef(0);
  const dataRef = useRef<ReturnType<typeof useWebSocket> | null>(null);
  const lastProcessedTrigger = useRef(reconnectTrigger);

  const websocketURL = useMemo(() => {
    if (!connect) {
      return null;
    }
    for (const key in params) {
      // If one of the parameters is undefined return null
      if (!params[key]) {
        return null;
      }
    }

    return BASE_SOCKET_URL + buildAPIRoute(path, params);
  }, [connect, path, params]);

  const updateStatus = useCallback(
    (status: WebSocketConnectionStatus, error?: string | null) => {
      if (!trackConnectionStatus) return;

      const { setWebSocketStatus } = useHilosStore.getState();
      setWebSocketStatus("direct", status, error);
    },
    [trackConnectionStatus]
  );

  const shouldReconnect = useCallback(
    (closeEvent: CloseEvent) => {
      if (closeEvent.code === 1000) {
        return false;
      }

      if (retryCountRef.current >= MAX_RETRIES) {
        console.warn(`[Direct WS] Max retries reached on ${path}`);
        updateStatus("failed", "Maximum reconnection attempts reached");
        return false;
      }

      retryCountRef.current += 1;
      console.log(`[Direct WS] Retry ${retryCountRef.current}/${MAX_RETRIES} on ${path}`);
      if (trackConnectionStatus) {
        const { incrementWebSocketRetry } = useHilosStore.getState();
        incrementWebSocketRetry("direct");
      }
      return true;
    },
    [trackConnectionStatus, updateStatus, path]
  );

  const data = useWebSocket<{ type: string; data: string }>(
    websocketURL,
    {
      reconnectInterval: (attemptNumber) =>
        Math.min(Math.pow(2, attemptNumber) * 1000, 16000),
      shouldReconnect,
      reconnectAttempts: MAX_RETRIES,
      onOpen: () => {
        retryCountRef.current = 0;
        if (trackConnectionStatus) {
          const { resetWebSocketRetry } = useHilosStore.getState();
          resetWebSocketRetry("direct");
        }
        updateStatus("connected", null);
      },
      onClose: (event) => {
        console.log(`[Direct WS] Closed on ${path}:`, event.code, event.reason);
        if (retryCountRef.current >= MAX_RETRIES) {
          updateStatus("failed", event.reason || "Connection closed");
        } else {
          updateStatus("disconnected", event.reason);
        }
      },
      onError: () => {
        console.error(`[Direct WS] Error on ${path}`);
        updateStatus("disconnected", "Connection error");
      },
    },
    Boolean(websocketURL)
  );

  dataRef.current = data;

  useEffect(() => {
    if (reconnectTrigger > lastProcessedTrigger.current && websocketURL) {
      console.log(`[Direct WS] Manual reconnection triggered on ${path}`);
      lastProcessedTrigger.current = reconnectTrigger;
      retryCountRef.current = 0;

      const ws = dataRef.current?.getWebSocket();
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close(MANUAL_RECONNECT_CODE, "manual_reconnect");
      }
    }
  }, [reconnectTrigger, websocketURL, path]);

  useEffect(() => {
    if (trackConnectionStatus && websocketURL) {
      const status = readyStateToStatus(data.readyState);
      const { webSocketState } = useHilosStore.getState();
      if (webSocketState.direct.status !== status) {
        updateStatus(status);
      }
    }
  }, [data.readyState, websocketURL, trackConnectionStatus, updateStatus]);

  useEffect(() => {
    if (websocketURL) {
      const handleUnload = () => resetGlobalState(websocketURL);

      window.addEventListener("unload", handleUnload);
      return () => {
        handleUnload();
        window.removeEventListener("unload", handleUnload);
      };
    }
  }, [websocketURL]);

  useEffect(() => {
    if (trackConnectionStatus && !websocketURL) {
      updateStatus("disconnected");
    }
  }, [websocketURL, trackConnectionStatus, updateStatus]);

  return data;
}

export default useHilosWebSockets;
